import {useLocation, useNavigate} from "react-router-dom";
import {Center, CloseButton, Container, Group, Loader, Paper, Space, Title} from "@mantine/core";
import {
  EntityIdentifier,
  ObjectComboMapping,
  objectComboMappingSelector,
  useGetObjectComboMappingQuery
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo, useState} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {
  ObjectComboMappingForm,
  ObjectComboMappingFormValues
} from "../../components/form/ObjectComboMappingForm/ObjectComboMappingForm";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";
import {useObjectComboMappingMutation} from "../../hooks/useObjectComboMappingMutation";

type Props = {
  id?: string;
  copy?: boolean;
  formDefaults?: Partial<ObjectComboMappingFormValues>
}

export const ObjectComboMappingFormPageRoute = () => {
  const {state} = useLocation();
  const id = state?.id;
  const copy = state?.copy;
  const formDefaults = state?.formDefaults;
  return <ObjectComboMappingRequestFormPage id={id} copy={copy} formDefaults={formDefaults}/>;
};

export const toFormDefaultsFromObjectComboMapping = (objectComboMapping: ObjectComboMapping | undefined): Partial<ObjectComboMappingFormValues> => {
  let leftObject: EntityIdentifier | undefined = undefined
  let rightObject: EntityIdentifier | undefined = undefined

  if (objectComboMapping && objectComboMapping.leftObjectId && objectComboMapping.leftObjectType) {
    leftObject = {
      id: objectComboMapping.leftObjectId,
      type: objectComboMapping.leftObjectType,
    }
  }

  if (objectComboMapping && objectComboMapping.rightObjectId && objectComboMapping.rightObjectType) {
    rightObject = {
      id: objectComboMapping.rightObjectId,
      type: objectComboMapping.rightObjectType,
    }
  }

  return {
    key: objectComboMapping?.key,
    value: objectComboMapping?.value,
    leftObject: leftObject,
    rightObject: rightObject,
  };
};

export const ObjectComboMappingRequestFormPage = (props: Props) => {
  const {id, copy = false, formDefaults} = props;

  const {regionId} = useApp()
  const navigate = useNavigate();
  const {t} = useTranslation(["common"]);

  const [access, setAccess] = useState(false);

  const selectObjectComboMapping = useMemo(() => {
    return objectComboMappingSelector()
  }, [])

  const {currObjectComboMapping, isLoading} = useGetObjectComboMappingQuery(access ? (id ?? skipToken) : skipToken, {
    selectFromResult: (result) => ({
      currObjectComboMapping: selectObjectComboMapping(result),
      isLoading: result.isLoading
    })
  });

  const _formDefaults: Partial<ObjectComboMappingFormValues> = useMemo(() => {
    return formDefaults ? formDefaults : toFormDefaultsFromObjectComboMapping(currObjectComboMapping);
  }, [currObjectComboMapping, formDefaults]);

  const [defaults, setDefaults] = useState<Partial<ObjectComboMappingFormValues>>(_formDefaults);
  const [formKey, setFormKey] = useState(0);

  const isEdit = useMemo(() => {
    return currObjectComboMapping && !copy
  }, [currObjectComboMapping, copy])

  const title: string = useMemo(() => {
    if (isEdit) {
      return t("page.objectcombomapping.form.title.edit", "Edit Object Combo Mapping", {ns: "common"});
    } else {
      return t("page.objectcombomapping.form.title.new", "New Object Combo Mapping", {ns: "common"});
    }
  }, [isEdit, t]);

  const {
    createObjectComboMapping,
    updateObjectComboMapping,
    isCreating,
    isUpdating,
  } = useObjectComboMappingMutation()

  const onObjectComboMappingSubmit = useCallback((formValues: ObjectComboMappingFormValues, newAfter: boolean) => {
    const patch: Partial<ObjectComboMapping> = {
      key: formValues.key,
      value: formValues.value,
      leftObjectId: formValues.leftObject?.id,
      leftObjectType: formValues.leftObject?.type,
      rightObjectId: formValues.rightObject?.id,
      rightObjectType: formValues.rightObject?.type,
    }

    if (isEdit) {
      const mapping: Partial<ObjectComboMapping> = {
        ...currObjectComboMapping,
        ...patch
      }
      updateObjectComboMapping(mapping, {onSuccess: () => navigate(-1)})
    } else {
      const mapping: Partial<ObjectComboMapping> = {
        ...patch
      }
      createObjectComboMapping(mapping, {
        onSuccess: () => {
          if (newAfter) {
            setDefaults({
              key: formValues.key,
              leftObject: formValues.leftObject,
            });
            setFormKey((k) => k + 1);
          } else {
            navigate(-1)
          }
        }
      })
    }
  }, [currObjectComboMapping, isEdit, updateObjectComboMapping, navigate, createObjectComboMapping]);

  return (
    <ProtectedPage
      permissions={[isEdit ? "objectmapping_edit" : "objectmapping_create"]}
      isLoading={isLoading}
      onAccess={setAccess}>
      {(classes) => (
        <Container className={classes.container} fluid={true} py="md">
          <Center>
            {isLoading && <Loader/>}
            {!isLoading && (
              <Paper miw={1000} shadow="sm" p="md" py="md">
                <Group position="apart">
                  <Title align="center">
                    {title}
                  </Title>
                  <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                </Group>
                <Space h="md"/>
                <ObjectComboMappingForm
                  key={`object_combo_form_${formKey}`}
                  options={{
                    newAfter: typeof currObjectComboMapping === 'undefined',
                    regionId: regionId,
                    leftObjectSelectDisabled: typeof currObjectComboMapping !== 'undefined' || typeof formDefaults?.leftObject !== 'undefined',
                    rightObjectSelectDisabled: typeof currObjectComboMapping !== 'undefined' || typeof formDefaults?.rightObject !== 'undefined'
                  }}
                  defaultValues={defaults}
                  submitting={isCreating || isUpdating}
                  onSubmit={onObjectComboMappingSubmit}/>
              </Paper>
            )}
          </Center>
        </Container>
      )}
    </ProtectedPage>
  );
};

